import React from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import { useModalStyles } from "./useModalStyles";
import { CircleCheck } from "lucide-react";

interface ConfirmationActionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  subtitle?: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  headerIcon?: React.ReactNode;
  showCancelButton?: boolean;
  confirmDisabled?: boolean; // CS-2018 - e.g. type-to-confirm flows keep confirm disabled until valid
}

const ConfirmationActionModal: React.FC<ConfirmationActionModalProps> = ({
  open,
  onClose,
  onConfirm,
  title = "Confirmation",
  subtitle = "Please review before continuing",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  headerIcon,
  showCancelButton = true,
  confirmDisabled = false, // CS-2018
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>
          {headerIcon ?? <CircleCheck color={tokens.accent} />}
        </Box>
        <Box>
          <Typography
            fontWeight={700}
            sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
            {subtitle}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          ...bodySx,
          py: 2.8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Typography
          component="div"
          sx={{
            fontSize: "1.05rem",
            lineHeight: 1.6,
            fontWeight: 500,
            color: tokens.textPrimary,
            maxWidth: "95%",
            wordBreak: "break-word",
            "& strong": {
              display: "inline",
              fontWeight: 700,
              color: tokens.textPrimary,
            },
            "& p": {
              m: 0,
            },
          }}
        >
          {message}
        </Typography>
      </Box>

      <Box sx={footerSx}>
        <Box sx={{ display: "flex", gap: 1.5}}>
          <Button
            fullWidth
            variant="contained"
            onClick={onConfirm}
            disabled={confirmDisabled} // CS-2018
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "1rem !important" }} />}
            sx={primaryBtnSx}
          >
            {confirmText}
          </Button>
          {showCancelButton && (
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
              sx={cancelBtnSx}
            >
              {cancelText}
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default ConfirmationActionModal;
